import React, { useId, useLayoutEffect, useRef, useState } from "react";

// Keep inline modals compatible with their callers while only the top dialog is
// interactive. Preserve pre-existing accessibility attributes when isolating it.
const modalStack = [];
const isolatedElements = new Map();
let savedBodyOverflow;
let modalObserver;
let modalActivationFocus;

function restoreModalIsolation() {
  isolatedElements.forEach(({ inert, ariaHidden }, element) => {
    if (inert === null) element.removeAttribute("inert");
    else element.setAttribute("inert", inert);
    if (ariaHidden === null) element.removeAttribute("aria-hidden");
    else element.setAttribute("aria-hidden", ariaHidden);
  });
  isolatedElements.clear();
}

function isolateTopModal() {
  restoreModalIsolation();
  const active = modalStack[modalStack.length - 1];
  modalStack.forEach((modal, index) => {
    modal.panel.setAttribute("aria-modal", String(modal === active));
    modal.container.style.zIndex = String(50 + index);
  });
  if (!active) return;
  let branch = active.container;
  while (branch.parentElement) {
    for (const sibling of branch.parentElement.children) {
      if (sibling === branch || sibling.tagName === "SCRIPT" || sibling.tagName === "STYLE") continue;
      isolatedElements.set(sibling, { inert: sibling.getAttribute("inert"), ariaHidden: sibling.getAttribute("aria-hidden") });
      sibling.setAttribute("inert", "");
      sibling.setAttribute("aria-hidden", "true");
    }
    if (branch.parentElement === document.body) break;
    branch = branch.parentElement;
  }
}

function modalFocusables(panel) {
  return Array.from(panel.querySelectorAll("a[href], button, input, select, textarea, [tabindex], [contenteditable='true']"))
    .filter((element) => {
      if (element.tabIndex < 0 || element.matches(":disabled, input[type='hidden']")) return false;
      for (let ancestor = element; ancestor && ancestor !== panel.parentElement; ancestor = ancestor.parentElement) {
        if (ancestor.matches("[hidden], [inert], [aria-hidden='true']")) return false;
        const style = window.getComputedStyle(ancestor);
        if (style.display === "none" || style.visibility === "hidden") return false;
      }
      return true;
    })
    .sort((a, b) => (a.tabIndex || Infinity) - (b.tabIndex || Infinity));
}

export function Button({ variant = "primary", size = "md", className = "", children, ...props }) {
  const base = "inline-flex items-center justify-center gap-2 font-heading font-bold focus-ring transition-transform duration-150 active:translate-y-[2px] disabled:opacity-50 disabled:pointer-events-none select-none";
  const sizes = {
    sm: "text-sm px-4 py-2 rounded-xl",
    md: "text-base px-6 py-3 rounded-2xl",
    lg: "text-lg px-8 py-4 rounded-full",
  };
  const variants = {
    primary: "bg-primary text-white shadow-claybtn hover:brightness-105 active:shadow-none",
    coral: "bg-coral text-white shadow-coral hover:brightness-105 active:shadow-none",
    sunny: "bg-sunny text-sunny-fg shadow-sunny hover:brightness-105 active:shadow-none",
    outline: "bg-white text-ink border-2 border-slate-200 hover:border-primary/50 shadow-sm",
    ghost: "bg-transparent text-ink-soft hover:bg-slate-100",
    dark: "bg-ink text-white hover:brightness-125",
  };
  return (
    <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Card({ kid = false, className = "", children, ...props }) {
  const style = kid
    ? "bg-white border-2 border-primary/20 shadow-clay rounded-4xl"
    : "bg-white border border-slate-200 shadow-soft rounded-3xl";
  return <div className={`${style} ${className}`} {...props}>{children}</div>;
}

export function Input({ label, className = "", ...props }) {
  return (
    <label className="block">
      {label && <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">{label}</span>}
      <input
        className={`w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-ink font-medium placeholder:text-ink-muted focus-ring focus:border-primary/50 transition-colors ${className}`}
        {...props}
      />
    </label>
  );
}

export function Modal({ open, onClose, title, children, testid }) {
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const [contentTitleId, setContentTitleId] = useState(null);
  onCloseRef.current = onClose;

  // Titleless callers (confirmation and trial dialogs) supply their own heading.
  useLayoutEffect(() => {
    if (!open || title) return;
    const heading = panelRef.current.querySelector("h1, h2, h3, h4, h5, h6");
    if (!heading) {
      setContentTitleId(null);
      return;
    }
    const originalId = heading.getAttribute("id");
    if (!originalId) heading.id = titleId;
    setContentTitleId(heading.id);
    return () => { if (!originalId) heading.removeAttribute("id"); };
  }, [open, title, children, titleId]);

  useLayoutEffect(() => {
    if (!open) return;
    const modal = { container: containerRef.current, panel: panelRef.current };
    const previousFocus = document.activeElement;
    const isTop = () => modalStack[modalStack.length - 1] === modal;
    const focusInside = () => (modalFocusables(modal.panel)[0] || modal.panel).focus();
    const onKeyDown = (event) => {
      if (!isTop()) return;
      if (event.key === "Escape" && !event.isComposing) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
      } else if (event.key === "Tab") {
        const focusables = modalFocusables(modal.panel);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first) {
          event.preventDefault();
          modal.panel.focus();
        } else if (!modal.panel.contains(document.activeElement) || document.activeElement === modal.panel ||
          (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
    };
    const onFocus = (event) => {
      if (isTop() && !modal.panel.contains(event.target)) focusInside();
    };
    // Also guard interaction in browsers without native inert support.
    const onOutsideInteraction = (event) => {
      if (isTop() && !modal.container.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    if (!modalStack.length) {
      modalActivationFocus = previousFocus;
      savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      modalObserver = new MutationObserver(isolateTopModal);
      modalObserver.observe(document.body, { childList: true, subtree: true });
    }
    // Child layout effects run first on a shared mount. Register an ancestor
    // below its already registered descendants so the inner dialog stays active.
    const descendantIndex = modalStack.findIndex((entry) => modal.container.contains(entry.container));
    if (descendantIndex === -1) modalStack.push(modal);
    else modalStack.splice(descendantIndex, 0, modal);
    restoreModalIsolation();
    // Move focus before hiding the opener from assistive technology.
    if (isTop()) focusInside();
    isolateTopModal();
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("pointerdown", onOutsideInteraction, true);
    document.addEventListener("click", onOutsideInteraction, true);
    return () => {
      const wasTop = isTop();
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("pointerdown", onOutsideInteraction, true);
      document.removeEventListener("click", onOutsideInteraction, true);
      modalStack.splice(modalStack.indexOf(modal), 1);
      isolateTopModal();
      if (!modalStack.length) {
        modalObserver.disconnect();
        document.body.style.overflow = savedBodyOverflow;
        // Ancestor cleanup can run before descendant cleanup when both close.
        // The first opener belongs to the whole activation, not either cleanup.
        if (modalActivationFocus?.isConnected) modalActivationFocus.focus();
        modalActivationFocus = null;
      } else if (wasTop) {
        const active = modalStack[modalStack.length - 1];
        if (previousFocus?.isConnected && active.panel.contains(previousFocus)) previousFocus.focus();
        else (modalFocusables(active.panel)[0] || active.panel).focus();
      }
    };
  }, [open]);

  if (!open) return null;
  const labelledBy = title ? titleId : contentTitleId;
  return (
    <div ref={containerRef} className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid={testid}>
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" aria-hidden="true" data-testid={`${testid || "modal"}-backdrop`} onClick={onClose} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={labelledBy || undefined} aria-label={labelledBy ? undefined : "Dialog"} tabIndex={-1} className="relative w-full sm:max-w-lg bg-white rounded-t-4xl sm:rounded-4xl border border-slate-200 shadow-2xl p-6 max-h-[90vh] overflow-y-auto no-scrollbar animate-pop motion-reduce:animate-none">
        {title && (
          <div className="flex items-center justify-between mb-4">
            <h3 id={titleId} className="text-xl font-heading font-extrabold text-ink">{title}</h3>
            <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink text-2xl leading-none focus-ring" data-testid="modal-close" aria-label="Close">×</button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function Pill({ children, tone = "primary", className = "" }) {
  const tones = {
    primary: "bg-primary/10 text-primary-dark",
    sunny: "bg-sunny/20 text-sunny-fg",
    coral: "bg-coral/15 text-coral-dark",
    slate: "bg-slate-100 text-ink-soft",
  };
  return <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${tones[tone]} ${className}`}>{children}</span>;
}

export function Coins({ amount, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 font-heading font-extrabold ${className}`}>
      <span className="grid place-items-center w-5 h-5 rounded-full bg-sunny text-sunny-fg text-[11px] leading-none shadow-sm">c</span>
      {amount}
    </span>
  );
}
